import { ISubmissionVisit, ISurvey } from '../../../constants/types';
import { displayText, surveyFieldsById } from '../submissionEditHistory';

/**
 * SKIN-576 — the pure part of re-filing a closed visit's photos from the patient page:
 * the API's shapes, the lesions as the reviewer reads them, which slots a photo may go to,
 * and what to say when the API refuses.
 */

// ── The API's shapes (sherwood-api docs/features/quality-check/image-placement.md) ──

/** One photo slot of a lesion: an `image` field of the lesion's group. */
export interface PlacementImageFieldDto {
    name: string;
    label: string;
    /** false when the lesion's own answers hide the slot; the API refuses a photo onto it */
    visible: boolean;
    localIds: string[];
}

/** A lesion instance of the submission that speaks for the visit. */
export interface PlacementLesionDto {
    groupRecordUuid: string;
    submissionPatientId: number;
    fieldGroupName: string;
    instanceNumber: number;
    imageFields: PlacementImageFieldDto[];
}

/** A photo of the study no record references, plausibly this visit's (server-side heuristic). */
export interface UnassignedImageDto {
    uuid: string;
    localId: string;
    createdAt: string;
    format: string | null;
    compression: string | null;
    qualityScore: number | null;
    identifiable: boolean | null;
    autoIdentifiable: boolean | null;
}

export interface VisitPhotoPlacementDto {
    window: { from: string; to: string };
    images: UnassignedImageDto[];
    lesions: PlacementLesionDto[];
}

export interface MovePhotoRequest {
    localId: string;
    fromGroupRecordUuid: string;
    toGroupRecordUuid: string;
    field: string;
    reason: string;
}

export interface AttachPhotoRequest {
    localId: string;
    toGroupRecordUuid: string;
    field: string;
    reason: string;
}

// ── The lesions as the reviewer reads them ──

export type PlacementSlot = PlacementImageFieldDto;

export interface PlacementLesion {
    groupRecordUuid: string;
    fieldGroupName: string;
    instanceNumber: number;
    /** the lesion's `body_map_code` answer as labels; null when unanswered */
    bodyRegion: string | null;
    /** the lesion's `image_target` answer as its label; null when unanswered */
    target: string | null;
    slots: PlacementSlot[];
}

/** The photo being re-filed: one already on a lesion, or an orphan. */
export type PhotoToPlace =
    | { kind: 'move'; localId: string; fromGroupRecordUuid: string; field: string }
    | { kind: 'attach'; localId: string };

/** A slot a photo may not go to, and why. `hidden`: the lesion's own answers hide it. */
export type SlotBlock = 'hidden' | null;

export interface PlacementTarget {
    lesion: PlacementLesion;
    slots: Array<PlacementSlot & { blocked: SlotBlock }>;
}

interface SubmissionRecord { surveyField: { id: number; name: string }; value: unknown }
interface SubmissionWithGroups {
    id: number;
    fieldGroups?: Array<{ instances: Array<{ groupRecordUuid?: string; surveyRecords: SubmissionRecord[] }> }>;
}

const BODY_REGION_FIELD = 'body_map_code';
const TARGET_FIELD = 'image_target';

/** The same name the edit history (SKIN-602) gives the lesion: `C-Lesion #2`. */
export function lesionTitle(lesion: Pick<PlacementLesion, 'fieldGroupName' | 'instanceNumber'>): string {
    return `${lesion.fieldGroupName} #${lesion.instanceNumber}`;
}

/**
 * The visit's lesions that belong to the submission on screen, labelled with their body
 * region and target from the submission's answers. The API's list is the source of truth for
 * which lesions exist and which photos each slot holds; the submission only names them.
 * A lesion of another autosave of the visit is dropped: the API refuses it as a target.
 */
export function describeLesions(lesions: PlacementLesionDto[], submission: SubmissionWithGroups, survey: ISurvey): PlacementLesion[] {
    const fields = surveyFieldsById(survey);
    const recordsByGroup = new Map<string, SubmissionRecord[]>();
    for (const group of submission.fieldGroups ?? []) {
        for (const instance of group.instances) {
            if (instance.groupRecordUuid) recordsByGroup.set(instance.groupRecordUuid, instance.surveyRecords);
        }
    }
    const answer = (records: SubmissionRecord[] | undefined, name: string): string | null => {
        const record = records?.find((candidate) => candidate.surveyField.name === name);
        if (!record || record.value === null || record.value === undefined || record.value === '') return null;
        const raw = typeof record.value === 'string' ? record.value : JSON.stringify(record.value);
        return displayText(raw, fields.get(record.surveyField.id)) || null;
    };
    return lesions
        .filter((lesion) => lesion.submissionPatientId === submission.id)
        .map((lesion) => {
            const records = recordsByGroup.get(lesion.groupRecordUuid);
            return {
                groupRecordUuid: lesion.groupRecordUuid,
                fieldGroupName: lesion.fieldGroupName,
                instanceNumber: lesion.instanceNumber,
                bodyRegion: answer(records, BODY_REGION_FIELD),
                target: answer(records, TARGET_FIELD),
                slots: lesion.imageFields,
            };
        });
}

/**
 * Where `photo` may go. A move never offers the lesion it is on (the API answers SAME_LESION);
 * a slot its lesion hides is listed but blocked, so the reviewer sees why it cannot be chosen.
 */
export function placementTargets(lesions: PlacementLesion[], photo: PhotoToPlace): PlacementTarget[] {
    return lesions
        .filter((lesion) => photo.kind !== 'move' || lesion.groupRecordUuid !== photo.fromGroupRecordUuid)
        .map((lesion) => ({
            lesion,
            slots: lesion.slots.map((slot) => ({ ...slot, blocked: slot.visible ? null : 'hidden' as const })),
        }));
}

// ── Who, where, and refusals ──

/** Codes the API answers a refused move / attach with (`ImagePlacementError`), each with copy. */
const KNOWN_ERRORS = [
    'REASON_REQUIRED', 'CONFIRMATION_REQUIRED', 'SAME_LESION', 'LESIONS_IN_DIFFERENT_SUBMISSIONS', 'NOT_AN_IMAGE_FIELD',
    'LESION_NOT_FOUND', 'FILE_NOT_FOUND',
    'VISIT_NOT_SUBMITTED', 'NOT_CANONICAL_SUBMISSION', 'IMAGE_NOT_ON_LESION', 'ALREADY_ON_TARGET', 'UNREADABLE_IMAGE_RECORD',
    'FILE_ALREADY_REFERENCED', 'CANONICAL_SUBMISSION_WOULD_CHANGE',
    'FIELD_HIDDEN_ON_TARGET',
];

const T = 'hospital.patient.photo-placement';

export type PlacementRefusal =
    | { kind: 'message'; translationId: string }
    | { kind: 'raw'; message: string };

/** What to tell the reviewer about a refused move / attach: a known code, a 403, or the server's words. */
export function decodePlacementRefusal(error: any): PlacementRefusal {
    const body = error?.response?.data;
    if (typeof body?.error === 'string' && KNOWN_ERRORS.includes(body.error)) {
        return { kind: 'message', translationId: `${T}.error-${body.error}` };
    }
    if (error?.response?.status === 403) return { kind: 'message', translationId: `${T}.error-forbidden` };
    if (typeof body?.errorMessage === 'string' && body.errorMessage) return { kind: 'raw', message: body.errorMessage };
    return { kind: 'message', translationId: `${T}.error-generic` };
}
